import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { apiService } from '../services/apiService';
import { useProjects } from '../context/ProjectContext';
import { AgentInlineSelect } from './ai-agent-ui';
import type { AgentMessageResponse } from '../types/agent';
import {
  normalizeAgentEnvironmentBaseUrl,
  validateAgentEnvironmentBaseUrl,
  validateAgentEnvironmentName,
} from '../utils/agentEnvironmentUtils';
import { environmentPlaceholderSx } from '../utils/environmentUtils';
import { isAgentFailureMessage } from '../utils/agentChatMessageStyle';
import { dispatchEnvironmentsChanged } from '../utils/environmentVariablesEvents';

export type AgentWidgetCreateEnvironmentFormProps = {
  chatId?: number | null;
  defaultProjectId?: number | '';
  defaultProjectName?: string;
  onClose: () => void;
  onUserMessage: (text: string) => void;
  onAgentReply: (res: AgentMessageResponse) => void | Promise<void>;
  onChatIdChange?: (chatId: number) => void;
  onLoadingChange?: (loading: boolean) => void;
};

const AgentWidgetCreateEnvironmentForm: React.FC<AgentWidgetCreateEnvironmentFormProps> = ({
  chatId,
  defaultProjectId = '',
  defaultProjectName = '',
  onClose,
  onUserMessage,
  onAgentReply,
  onChatIdChange,
  onLoadingChange,
}) => {
  const { refreshProjects, setSelectedProject } = useProjects();
  const [availableProjects, setAvailableProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [envName, setEnvName] = useState('');
  const [envUrl, setEnvUrl] = useState('');
  const [envUrlError, setEnvUrlError] = useState<string | null>(null);
  const [envNameError, setEnvNameError] = useState<string | null>(null);
  const [envDescription, setEnvDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const mergeProjectOption = useCallback(
    (list: Array<{ id: number; name: string }>) => {
      if (defaultProjectId === '' || Number.isNaN(Number(defaultProjectId))) {
        return list;
      }
      const id = Number(defaultProjectId);
      if (list.some((p) => p.id === id)) {
        return list;
      }
      const name = defaultProjectName.trim() || `Project ${id}`;
      return [...list, { id, name }].sort((a, b) => a.name.localeCompare(b.name));
    },
    [defaultProjectId, defaultProjectName],
  );

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      await refreshProjects();
      const fetched = await apiService.getProjects();
      const mapped = fetched.map((p) => ({ id: p.id, name: p.name }));
      setAvailableProjects(mergeProjectOption(mapped));
    } catch {
      if (defaultProjectId !== '' && !Number.isNaN(Number(defaultProjectId))) {
        setAvailableProjects(
          mergeProjectOption([
            {
              id: Number(defaultProjectId),
              name: defaultProjectName.trim() || `Project ${defaultProjectId}`,
            },
          ]),
        );
      } else {
        setAvailableProjects([]);
      }
    } finally {
      setLoadingProjects(false);
    }
  }, [defaultProjectId, defaultProjectName, mergeProjectOption, refreshProjects]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (loadingProjects) return;
    if (defaultProjectId !== '' && availableProjects.some((p) => p.id === defaultProjectId)) {
      setSelectedProjectId(defaultProjectId);
      return;
    }
    if (availableProjects.length === 1) {
      setSelectedProjectId(availableProjects[0].id);
    }
  }, [defaultProjectId, availableProjects, loadingProjects]);

  const selectValue = useMemo(() => {
    if (selectedProjectId === '') return '';
    return availableProjects.some((p) => p.id === selectedProjectId) ? selectedProjectId : '';
  }, [availableProjects, selectedProjectId]);

  const handleCreate = async () => {
    const name = envName.trim();
    const urlInput = envUrl.trim();
    const desc = envDescription.trim();
    const nameValidationError = validateAgentEnvironmentName(name);
    const urlValidationError = validateAgentEnvironmentBaseUrl(urlInput);
    setEnvNameError(nameValidationError);
    setEnvUrlError(urlValidationError);
    if (!selectedProjectId || !name || !urlInput || nameValidationError || urlValidationError) return;

    const url = normalizeAgentEnvironmentBaseUrl(urlInput);
    const selected = availableProjects.find((p) => p.id === selectedProjectId);
    const userText = `Create environment "${name}" in project "${selected?.name || selectedProjectId}", base URL ${url}`;
    onUserMessage(userText);
    setSubmitting(true);
    onLoadingChange?.(true);
    try {
      const res = await apiService.agentMessage({
        action: 'create_environment',
        chatId: chatId ?? undefined,
        payload: {
          projectId: selectedProjectId,
          name,
          url,
          ...(desc ? { description: desc } : {}),
        },
      });
      if (res.state?.chatId != null) {
        onChatIdChange?.(res.state.chatId as number);
      }
      if (res.state?.projectId != null) {
        try {
          const list = await apiService.getProjects();
          const match = list.find((p) => p.id === res.state!.projectId);
          if (match) setSelectedProject(match);
        } catch {
          /* ignore */
        }
      }
      await onAgentReply(res);
      if (!res.error && !isAgentFailureMessage(res.reply || '', res.actions)) {
        const projectId =
          typeof res.state?.projectId === 'number'
            ? res.state.projectId
            : Number(selectedProjectId);
        const environmentId =
          typeof res.state?.environmentId === 'number' ? res.state.environmentId : undefined;
        if (!Number.isNaN(projectId) && projectId > 0) {
          dispatchEnvironmentsChanged({
            projectId,
            environmentId,
            source: 'widget',
          });
        }
        onClose();
      }
    } finally {
      setSubmitting(false);
      onLoadingChange?.(false);
    }
  };

  const busy = submitting || loadingProjects;
  const nameInvalid = envName.trim() ? validateAgentEnvironmentName(envName) : null;
  const urlInvalid = envUrl.trim() ? validateAgentEnvironmentBaseUrl(envUrl) : null;

  return (
    <Box
      sx={{
        mt: 1,
        mb: 0.5,
        p: 1.25,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(15,23,42,0.12)',
        ...environmentPlaceholderSx,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Create environment
      </Typography>

      <Box sx={{ mb: 1 }}>
        <AgentInlineSelect
          id="widget-agent-env-project"
          label="Project"
          fullWidth
          value={selectValue === '' ? '' : selectValue}
          disabled={busy}
          emptyLabel={loadingProjects ? 'Loading projects…' : 'No projects found'}
          options={availableProjects.map((project) => ({
            value: project.id,
            label: project.name,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            setSelectedProjectId(Number.isNaN(parsed) || raw === '' ? '' : parsed);
          }}
        />
      </Box>

      <TextField
        label="Environment name"
        size="small"
        fullWidth
        value={envName}
        onChange={(e) => {
          const next = e.target.value;
          setEnvName(next);
          setEnvNameError(validateAgentEnvironmentName(next));
        }}
        onBlur={() => setEnvNameError(validateAgentEnvironmentName(envName))}
        error={!!envNameError}
        helperText={envNameError || undefined}
        disabled={busy}
        sx={{ mb: 1 }}
      />
      <TextField
        label="Base URL"
        size="small"
        fullWidth
        value={envUrl}
        onChange={(e) => {
          const next = e.target.value;
          setEnvUrl(next);
          setEnvUrlError(validateAgentEnvironmentBaseUrl(next));
        }}
        onBlur={() => setEnvUrlError(validateAgentEnvironmentBaseUrl(envUrl))}
        placeholder="https://api.example.com"
        error={!!envUrlError}
        helperText={envUrlError || undefined}
        disabled={busy}
        sx={{ mb: 1 }}
      />
      <TextField
        label="Description (optional)"
        size="small"
        fullWidth
        value={envDescription}
        onChange={(e) => setEnvDescription(e.target.value)}
        disabled={busy}
        sx={{ mb: 1 }}
      />

      {!loadingProjects && availableProjects.length === 0 && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
          Create a project first, then add an environment.
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => void handleCreate()}
          disabled={
            busy ||
            !selectedProjectId ||
            !envName.trim() ||
            !envUrl.trim() ||
            !!nameInvalid ||
            !!urlInvalid
          }
          sx={{ textTransform: 'none' }}
        >
          {busy ? <CircularProgress size={16} color="inherit" /> : 'Create'}
        </Button>
      </Box>

      <Divider sx={{ mt: 1.25 }} />
    </Box>
  );
};

export default AgentWidgetCreateEnvironmentForm;
